import { AfterViewInit, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth/auth.service';
import { AuthFailureNoticeService } from '../../../services/auth/auth-failure-notice.service';
import { CrossTabAuthSyncService } from '../../../services/auth/cross-tab-auth-sync.service';
import { DIAGNOSTIC_TOAST_LIFE_MS } from '../../../utils/sheet-load-trace.util';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { filter, finalize, tap } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { emailValidator } from '../../../app.validators';
import {
  LOGIN_UNRECOGNISED_FAILURE_MESSAGE,
  LoginFailureView,
  resolveLoginFailure,
} from '../../../utils/login-errors.util';
import { InteractiveToastComponent } from '../../common/interactive-toast/interactive-toast.component';

@Component({
  selector: 'login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    SkeletonModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    InteractiveToastComponent,
  ],
  providers: [MessageService],
  templateUrl: './login.component.html',
})
export class LoginComponent implements AfterViewInit {
  isLoadingYYCLogo: boolean = true;
  isSubmitting: boolean = false;
  showError = signal<string | null>(null);

  failureHighlight = false;

  /** True while the backend's login lockout runs; disables the Login button. */
  isLockedOut = signal<boolean>(false);
  private lockoutTimer: ReturnType<typeof setTimeout> | null = null;

  loginService = inject(AuthService);
  router = inject(Router);
  messageService = inject(MessageService);
  private authFailureNotice = inject(AuthFailureNoticeService);
  private crossTabAuth = inject(CrossTabAuthSyncService);
  private destroyRef = inject(DestroyRef);

  /** Re-entrancy guard so a burst of login events triggers at most one reload. */
  private reloading = false;
  // Gates the autocomplete tokens emitted by the login inputs.
  // See RuntimeConfig.authAutocompleteEnabled for the developer-note context.
  private runtimeConfig = inject(RuntimeConfigService);
  autocompleteEnabled = this.runtimeConfig.authAutocompleteEnabled;

  loginForm = new FormGroup({
    email: new FormControl<string>('', [
      Validators.required,
      emailValidator()
    ]),
    password: new FormControl('', Validators.required),
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.clearLockoutTimer());

    // Developer Note (Single User Session Per Device): A forced logout
    // (single-active-session: superseded by another device, or an expired/invalid
    // session caught on the next API call) redirects here carrying the machine
    // reason in `history.state.authReason`.
    //
    // The marker itself no longer drives any login-screen copy (see
    // `ForcedLogoutReason`'s docblock) — it is still consumed one-shot purely
    // so a stale `authReason` cannot survive a reload / back-forward navigation
    // and re-trip `redirectIfAuthenticatedGuard`'s forced-logout short-circuit.
    // `authReason` is written into `history.state` by `forceLogout()`'s
    // navigation, and a later `window.location.reload()` (the cross-tab
    // login-follow in subscribeToCrossTabLogin below) PRESERVES that history
    // entry's state. Stripping it here stops a stale `authReason` from
    // surviving the reload and being read by `redirectIfAuthenticatedGuard` as
    // a fresh forced logout — which would short-circuit the backend re-check
    // and strand the tab on /auth/login instead of following the new sign-in
    // to the dashboard.
    const state = history.state as { authReason?: unknown } | null;
    const authReason = state?.authReason;
    if (typeof authReason === 'string' && authReason.length > 0) {
      history.replaceState({ ...state, authReason: undefined }, '');
    }

    this.subscribeToCrossTabLogin();
  }

  ngAfterViewInit(): void {
    // A classified page/sheet/guard failure stages its FULLY COMPOSED toast
    // line here before the forced-logout (or auth-guard) redirect lands — see
    // `AuthFailureNoticeService`. Read-once: a later unrelated visit to this
    // page sees nothing. Deliberately not just a bare code: the staged value
    // is not always AUTH (e.g. `isAuthenticatedGuard` stages a NET/SRV/RESP
    // failure when it could not even confirm the session), so the sentence
    // has to travel WITH the code rather than being reconstructed here from
    // a hardcoded assumption of which class it was.
    //
    // Deliberately NOT read in the constructor OR `ngOnInit`:
    // `<interactive-toast>`'s own `p-toast` subscribes to
    // `MessageService.messageObserver` — a plain `Subject`, not a
    // `ReplaySubject` — from ITS OWN `ngOnInit`. Angular's lifecycle hooks
    // fire TOP-DOWN for `ngOnInit` (a parent's `ngOnInit` runs BEFORE its
    // children's), so this component's own `ngOnInit` is no safer a place
    // to call `messageService.add()` than its constructor — both run before
    // the child has had a chance to subscribe, so the message lands in the
    // Subject with nobody listening and is lost with no error. `ngAfterViewInit`
    // is the one hook Angular fires the other way round (bottom-up): a
    // component's view, BY DEFINITION, is only "after init" once every child
    // view inside it — including `<interactive-toast>`'s — has already
    // completed its own `ngOnInit`. That ordering is what this relies on.
    const authFailureSummary = this.authFailureNotice.consume();
    if (authFailureSummary) {
      this.messageService.add({
        key: 'login',
        severity: 'error',
        summary: authFailureSummary,
        life: DIAGNOSTIC_TOAST_LIFE_MS,
      });
    }
  }

  /**
   * Cross-tab LOGIN follow — owned by the login page ONLY. When the user signs
   * in on a sibling tab, `AuthService.login()` broadcasts a 'customer' login and
   * this tab hard-reloads so `redirectIfAuthenticatedGuard` (on the /auth group)
   * re-verifies the shared HttpOnly cookie and forwards it to /mbrs/dashboard.
   *
   * The other four /auth pages (forgot-password, reset-link, reset-password,
   * reset-password-status) deliberately do NOT follow: a user mid-reset stays
   * put and only reaches the dashboard via "Back to Login", which lands here
   * through the same guard. This used to live in `AuthLayoutComponent`, which
   * made all five pages follow.
   *
   * A hard reload rather than `router.navigate`: this tab's in-memory
   * `isAuthenticated` is `false`, so a SPA navigation would bounce off
   * `isAuthenticatedGuard`. The broadcast is only a trigger, never proof of
   * authentication. Scope-filtered to 'customer' so a dev-portal login never
   * disturbs this tab.
   */
  private subscribeToCrossTabLogin(): void {
    this.crossTabAuth.login$
      .pipe(
        filter((message) => message.scope === 'customer'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.reloading) return;
        this.reloading = true;
        this.reloadPage();
      });
  }

  /** Seam so specs can assert the reload without reloading the Karma page. */
  protected reloadPage(): void {
    window.location.reload();
  }

  get inputtedEmail() {
    return this.loginForm.get('email');
  }

  get inputtedPassword() {
    return this.loginForm.get('password');
  }

  isButtonDisabled(): boolean {
    return this.loginForm.invalid || this.isSubmitting || this.isLockedOut();
  }

  onAnyFieldFocus() {
    if (this.failureHighlight) this.failureHighlight = false;
  }

  submitForm() {
    // The disabled button already blocks a click; this also blocks Enter.
    if (this.isLockedOut()) {
      return;
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.showError.set(null);

    const credentials = this.loginForm.value as {
      email: string;
      password: string;
    };

    this.loginService
      .login(credentials)
      .pipe(
        tap((response) => {
          if (response.status === false) {
            this.showFailure(resolveLoginFailure(response));
          }
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        // `AuthService.handleError` re-throws a request that never reached the
        // server (status 0) rather than converting it, and the global
        // interceptor deliberately does not redirect for `/api/auth/` — so
        // without this the form would stay silent. No retry: on a 429 lockout
        // every retry is refused too.
        error: () =>
          this.showFailure({
            message: LOGIN_UNRECOGNISED_FAILURE_MESSAGE,
            highlightFields: false,
          }),
      });
  }

  private showFailure(failure: LoginFailureView): void {
    this.showError.set(failure.message);
    this.failureHighlight = failure.highlightFields;
    if (failure.lockoutDurationMs !== undefined) {
      this.startLockout(failure.lockoutDurationMs);
    }
  }

  /**
   * Keeps the Login button disabled until the backend's lockout ends, then
   * re-enables it and clears the lockout message (which would otherwise still
   * say "try again in N minutes" after it no longer applies).
   *
   * Deliberately IN MEMORY only (P.O. decision): a page refresh forgets the
   * lockout and re-enables the button. The next attempt is refused with a
   * fresh 429 whose `retryAfterSeconds` restarts this timer for the time that
   * is actually left. Persisting it to web storage would also be the kind of
   * client-held auth state this app avoids.
   */
  private startLockout(durationMs: number): void {
    this.clearLockoutTimer();
    this.isLockedOut.set(true);
    this.lockoutTimer = setTimeout(() => {
      this.lockoutTimer = null;
      this.isLockedOut.set(false);
      this.showError.set(null);
    }, durationMs);
  }

  private clearLockoutTimer(): void {
    if (this.lockoutTimer !== null) {
      clearTimeout(this.lockoutTimer);
      this.lockoutTimer = null;
    }
  }
}
